import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LakeEntityType, type LakeExportRun } from '@regigov/contracts';
import { lakeApi } from '../api/lake';
import { ProblemAlert, StatusBadge, formatDateTime } from './ui';

export function LakeExportPage() {
  const [params, setParams] = useSearchParams();
  const entityType = LakeEntityType.safeParse(params.get('entityType')).data;
  const status = useQuery({ queryKey: ['lake', 'status'], queryFn: lakeApi.status, refetchInterval: 15_000 });
  const runs = useQuery({ queryKey: ['lake', 'runs'], queryFn: lakeApi.runs, refetchInterval: 15_000 });
  const files = useQuery({ queryKey: ['lake', 'files', entityType], queryFn: () => lakeApi.files({ entityType }), refetchInterval: 15_000 });

  return (
    <>
      <p>
        <Link to="/cer/exchange">← Information exchange</Link>
      </p>
      <h1>Data lake export</h1>
      <p className="muted">
        Major business transactions write a business event to <code>lake.outbox</code> in the same transaction. The lake exporter claims each entity type's new events in
        batches, uploads each batch as a JSON Lines file to ADLS (Azurite locally) and then marks it exported. A claimed file that hasn't been uploaded is uploaded again to the
        same path on the next run. See <code>docs/lake-export.md</code>.
      </p>
      <ProblemAlert error={status.error ?? runs.error ?? files.error} />
      {runs.data && <LastRun run={runs.data.items[0]} />}
      {status.data && (
        <table className="table">
          <thead>
            <tr>
              <th>Entity type</th>
              <th>Events</th>
              <th>Exported</th>
              <th>Claimed, not uploaded</th>
              <th>Waiting</th>
              <th>Oldest unexported</th>
              <th>Last export</th>
            </tr>
          </thead>
          <tbody>
            {status.data.items.map((s) => (
              <tr key={s.entityType}>
                <td>
                  <code>{s.entityType}</code>
                </td>
                <td>{s.events}</td>
                <td>{s.exported}</td>
                <td>{s.claimed}</td>
                <td>{s.waiting}</td>
                <td>{s.oldestUnexportedAt ? formatDateTime(s.oldestUnexportedAt) : '—'}</td>
                <td>
                  {s.lastExportedAt ? (
                    <>
                      {formatDateTime(s.lastExportedAt)}
                      <br />
                      <code>{s.lastExportedPath}</code>
                    </>
                  ) : (
                    'Never'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <h2>Recent runs</h2>
      {runs.data && runs.data.items.length === 0 && <p className="muted">The exporter hasn't run yet.</p>}
      {runs.data && runs.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Started</th>
              <th>Finished</th>
              <th>Outcome</th>
              <th>Exported</th>
              <th>Failures</th>
            </tr>
          </thead>
          <tbody>
            {runs.data.items.map((r) => (
              <tr key={r.id}>
                <td>{formatDateTime(r.startedAt)}</td>
                <td>{formatDateTime(r.finishedAt)}</td>
                <td>
                  <StatusBadge status={failedRun(r) ? 'failed' : 'success'} />
                </td>
                <td>{r.exported.length === 0 ? <span className="muted">Nothing new</span> : r.exported.map((e) => <div key={e.entityType}>{`${e.entityType}: ${plural(e.rows, 'row')}, ${plural(e.files, 'file')}`}</div>)}</td>
                <td>
                  <Failures run={r} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <h2>Recent files</h2>
      <div className="row">
        <select
          value={entityType ?? ''}
          onChange={(e) => {
            const p = new URLSearchParams(params);
            if (e.target.value) p.set('entityType', e.target.value);
            else p.delete('entityType');
            setParams(p);
          }}
          aria-label="Entity type filter"
        >
          <option value="">All entity types</option>
          {LakeEntityType.options.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      {files.data && files.data.items.length === 0 && <p className="muted">No files yet.</p>}
      {files.data && files.data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Claimed</th>
              <th>Entity type</th>
              <th>Path</th>
              <th>Rows</th>
              <th>Status</th>
              <th>Exported</th>
            </tr>
          </thead>
          <tbody>
            {files.data.items.map((f) => (
              <tr key={f.batchId}>
                <td>{formatDateTime(f.claimedAt)}</td>
                <td>
                  <code>{f.entityType}</code>
                </td>
                <td>
                  <code>{f.path}</code>
                </td>
                <td>{f.rows}</td>
                <td>
                  <StatusBadge status={f.exportedAt ? 'exported' : 'claimed'} />
                </td>
                <td>{f.exportedAt ? formatDateTime(f.exportedAt) : 'Not exported'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const failedRun = (r: LakeExportRun) => r.error !== null || r.failed.length > 0;

function Failures({ run }: { run: LakeExportRun }) {
  if (!failedRun(run)) return <span className="muted">—</span>;
  return (
    <>
      {run.error && <div>{run.error}</div>}
      {run.failed.map((f) => (
        <div key={f.entityType}>
          <code>{f.entityType}</code>: {f.error}
        </div>
      ))}
    </>
  );
}

function LastRun({ run }: { run: LakeExportRun | undefined }) {
  if (!run) return <p className="notice">The exporter hasn't run yet.</p>;
  if (failedRun(run)) {
    return (
      <div role="alert" className="alert">
        <strong>The last export run failed ({formatDateTime(run.finishedAt)})</strong>
        <Failures run={run} />
      </div>
    );
  }
  return <p className="muted">Last export run: {formatDateTime(run.finishedAt)}.</p>;
}
